import '@syncfusion/ej2-react-inplace-editor/styles/inplace-editor/material.css';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { DatePickerComponent, MaskedDateTime, Inject } from '@syncfusion/ej2-react-calendars';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { MaskedTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import ControlledProviderDataTextBox from 'components/Common/CustomTextBoxWrapper/ControlledProviderDataTextBox';
import 'components/ProviderData/IpaAdministration/organisms/IpaAdministration.css';
import { showIPAEndDateIsInThePastWarningDialog } from 'components/ProviderData/IpaAdministration/atoms/utils';
import styles from 'components/ProviderData/IpaAdministration/organisms/IpaAdministrationDetailView/IpaAdministrationDetailViewMainInfo.module.css';

type IpaAdministrationDetailViewMainInfoProps = {
    ipa: Record<string, any>;
    setIpa: (newIpa?: Record<string, any>) => void;
    contractInfoForm: any;
    setContractInfoValue: any;
    getContractInfoValues: any;
    editFlag: any;
    errors: Record<string, any>;
    risks: Record<string, any>;
    types: Record<string, any>;
};

function IpaAdministrationDetailViewMainInfo(props: IpaAdministrationDetailViewMainInfoProps) {
    const {
        control
    } = useForm<any>();

    const checkEndDateIsInThePast = (endDateArgs) => {
        if (endDateArgs.isInteracted){
            const fomattedToday = `${(new Date()).getMonth() + 1}/${(new Date()).getDate()}/${(new Date()).getFullYear()}`;
            const formattedEndDate = `${(new Date(endDateArgs.value)).getMonth() + 1}/${(new Date(endDateArgs.value)).getDate()}/${(new Date(endDateArgs.value)).getFullYear()}`;
            const isEndDateInThePast = new Date(formattedEndDate) < new Date(fomattedToday);

            if (isEndDateInThePast) {
                showIPAEndDateIsInThePastWarningDialog();
            }
        }
    }

    return (
        props?.contractInfoForm && (
            <div
                className={`${styles['ipa-details-container']} ${
                    props?.editFlag ? styles['no-edit-flag'] : styles['edit-flag']
                }`}
            >
                <Row>
                    <Col md={6}>
                        <div className={`e-float-input e-input-group e-input-group-margin`}>
                            <label className={`${styles['ipa-code-label']} e-float-text boldCss`}>IPA Code</label>
                            <input
                                type='text'
                                maxLength={12}
                                {...props?.contractInfoForm('ipaCode')}
                                readOnly={true}
                                autoFocus={true}
                            />
                            <span className='e-float-line'></span>
                        </div>
                        <Row>
                            <Col md={6}>
                                <div className={`${styles['ipa-datepicker']} datepicker e-input-group-margin`}>
                                    <label className='e-label-select boldCss'>Effective Date *</label>
                                    <DatePickerComponent
                                        format='MM/dd/yyyy'
                                        enableMask={true}
                                        id='calendar_start'
                                        {...props?.contractInfoForm('effDate', {
                                            required: 'Effective Date is required',
                                        })}
                                        change={({ value }) => props?.setContractInfoValue('effDate', value)}
                                        min={undefined}
                                        max={new Date('12/31/9999')}
                                        value={new Date(props?.ipa.effDate)}
                                        strictMode
                                    >
                                        <Inject services={[MaskedDateTime]} />
                                    </DatePickerComponent>
                                    {props?.errors?.effDate && (
                                        <span className='error-form'>{props?.errors?.effDate?.message}</span>
                                    )}
                                </div>
                            </Col>

                            <Col md={6}>
                                <div
                                    style={{ width: '100%' }}
                                    className='datepicker e-input-group-margin'
                                >
                                    <label className='e-label-select boldCss'>End Date *</label>
                                    <DatePickerComponent
                                        format='MM/dd/yyyy'
                                        enableMask={true}
                                        id='calendar_end'
                                        {...props?.contractInfoForm('endDate', {
                                            required: 'End Date is required',
                                            validate: (value) => {
                                                const effDate = new Date(props?.getContractInfoValues('effDate'));
                                                const endDate = new Date(value);
                                                if (endDate <= effDate)
                                                    return `End date must be Greater than Effective Date`;
                                            },
                                        })}
                                        change={(args) => {
                                            props?.setContractInfoValue('endDate', args?.value);
                                            checkEndDateIsInThePast(args);
                                        }}
                                        min={undefined}
                                        max={new Date('12/31/9999')}
                                        value={new Date('12/31/9999')}
                                        strictMode
                                    >
                                        <Inject services={[MaskedDateTime]} />
                                    </DatePickerComponent>
                                    {props?.errors?.endDate && (
                                        <span className='error-form'>{props?.errors?.endDate?.message}</span>
                                    )}
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <div>
                                    <label className='col-md-12 mb-1'>IPA Name / IPA Description</label>
                                    <ControlledProviderDataTextBox
                                        name='ipaDesc'
                                        control={control}
                                        boxAttrs={{
                                            htmlAttributes: {
                                                value: props?.ipa.ipaDesc,
                                                maxLength: '100',
                                            },
                                        }}
                                        onChangeHandler={(value) => {
                                            props?.setContractInfoValue('ipaDesc', value)
                                        }}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <div className='e-input-group-margin'>
                                    <div className='control-label label-mask'>IPA Phone 1</div>
                                    <MaskedTextBoxComponent
                                        mask='(000) 000-0000'
                                        {...props?.contractInfoForm('ipaPhone', {
                                            maxLength: 15,
                                            minLength: 10,
                                        })}
                                    />
                                </div>
                                {props?.errors?.ipaPhone && (
                                    <p className='error-form'>Incorrect format phone number (123)123-1234</p>
                                )}
                            </Col>
                            <Col md={6}>
                                <div className='e-input-group-margin'>
                                    <div className='control-label label-mask'>IPA Phone 2</div>
                                    <MaskedTextBoxComponent
                                        mask='(000) 000-0000'
                                        {...props?.contractInfoForm('ipaPhone2', {
                                            maxLength: 15,
                                            minLength: 10,
                                        })}
                                    />
                                </div>
                                {props?.errors?.ipaPhone2 && (
                                    <p className='error-form'>Incorrect format phone number (123) 123-1234</p>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.contractInfoForm('ipaNameIdCard', {
                                            maxLength: {
                                                value: 80,
                                                message: 'Member Card/IPA Name must be maximum 80 characters length',
                                            },
                                        })}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text boldCss'>Member Card/IPA Name</label>
                                </div>
                                {props?.errors?.ipaNameIdCard && (
                                    <p className='error-form'>{props.errors.ipaNameIdCard.message}</p>
                                )}
                            </Col>
                        </Row>
                    </Col>

                    <Col md={6}>
                        <Row>
                            <Col md={6}>
                                <div className='e-input-group-margin'>
                                    <label className='e-label-select'>Current Contract Type *</label>
                                    <DropDownListComponent
                                        id='risk'
                                        dataSource={props?.risks}
                                        placeholder='Select a Contract Type'
                                        fields={{ text: 'text', value: 'id' }}
                                        change={({ itemData }) => props?.setContractInfoValue('risk', itemData.id)}
                                        popupHeight='220px'
                                        {...props?.contractInfoForm('risk', { required: 'Contract Type is required' })}
                                    />
                                </div>
                                {props?.errors?.risk && (
                                    <span className='error-form'>{props?.errors?.risk?.message}</span>
                                )}
                            </Col>
                            <Col md={6}>
                                <div className='e-input-group-margin'>
                                    <label className='e-label-select'>Current Type *</label>
                                    <DropDownListComponent
                                        id='type'
                                        dataSource={props?.types}
                                        placeholder='Select a type'
                                        {...props?.contractInfoForm('type', { required: 'Type is required' })}
                                        change={({ value }) => {
                                            props?.setContractInfoValue('type', value);
                                        }}
                                        popupHeight='220px'
                                    />
                                </div>
                                {props?.errors?.type && <p className='error-form'>{props?.errors?.type?.message}</p>}
                            </Col>
                        </Row>
                        <div className={`e-float-input e-input-group e-input-group-margin`}>
                            <input
                                type='text'
                                {...props?.contractInfoForm('notes', {
                                    maxLength: {
                                        value: 50,
                                        message: 'Notes must be maximum 50 characters length',
                                    },
                                })}
                            />
                            <span className='e-float-line'></span>
                            <label className='e-float-text boldCss'>Notes</label>
                        </div>
                        {props?.errors?.notes && (
                            <p className='error-form'>{props.errors.notes.message}</p>
                        )}
                        <Row>
                            <Col md={8}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.contractInfoForm('ip')}
                                        maxLength={15}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text boldCss'>IP</label>
                                </div>
                            </Col>
                            <Col
                                md={4}
                                style={{ display: 'flex' }}
                            >
                                <div
                                    style={{ margin: 'auto' }}
                                    className='e-input-group-margin'
                                >
                                    <CheckBoxComponent
                                        checked={props?.ipa?.termed}
                                        label='Hide On Web'
                                        change={({ checked }) => {
                                            props?.setIpa({ ...props?.ipa, termed: checked });
                                            props?.setContractInfoValue('termed', checked, { shouldDirty: true });
                                        }}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <div className={`e-float-input e-input-group e-input-group-margin`}>
                            <input
                                type='text'
                                {...props?.contractInfoForm('webSite', {
                                    maxLength: {
                                        value: 100,
                                        message: 'Website must be maximum 100 characters length',
                                    },
                                })}
                            />
                            <button
                                className={'btn btn-primary'}
                                type={'button'}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (props?.getContractInfoValues('webSite')) {
                                        const website = '//' + props?.getContractInfoValues('webSite');
                                        window.open(website, '_blank');
                                    }
                                }}
                            >
                                <i className='fa fa-search'></i>
                            </button>
                            <span className='e-float-line'></span>
                            <label className='e-float-text boldCss'>Website</label>
                        </div>
                        {props?.errors?.webSite && (
                            <p className='error-form'>{props.errors.webSite.message}</p>
                        )}
                        <Row>
                            <Col md={6}>
                                <div className='e-input-group-margin'>
                                    <div className={`${styles['member-card-label']} control-label label-mask`}
                                    >Member Card/IPA Phone</div>
                                    <MaskedTextBoxComponent
                                        mask='(000) 000-0000'
                                        {...props?.contractInfoForm('ipaPhoneIdCard', {
                                            maxLength: 15,
                                            minLength: 10,
                                        })}
                                    />
                                </div>
                                {props?.errors?.ipaPhoneIdCard && (
                                    <p className='error-form'>Incorrect format phone number (123)123-1234</p>
                                )}
                            </Col>
                            <Col md={6}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.contractInfoForm('internalPointofContact', {
                                            maxLength: {
                                                value: 255,
                                                message: 'Internal Point of Contact must be maximum 255 characters length',
                                            },
                                        })}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text boldCss'>Internal Point of Contact</label>
                                </div>
                                {props?.errors?.internalPointofContact && (
                                    <p className='error-form'>{props.errors.internalPointofContact.message}</p>
                                )}
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </div>
        )
    );
}

export default IpaAdministrationDetailViewMainInfo;
